import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { PUBLIC_PATH } from '../../../../../shared/config/routes-config';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getPaymentActivities } from '../../../../shared/services/data.service';

type HistoryValue = string | number | boolean | null | undefined | HistoryObject | HistoryValue[];

interface HistoryObject {
  [key: string]: HistoryValue;
}

export interface PaymentHistoryModalProps {
  config: {
    showModal: boolean;
  };
  data: {
    paymentId: string;
  };
  eventHandlers: {
    onClose: () => void;
  };
}

function formatValue(value: any): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value.trim() ? value : '-';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function isPlainObject(value: HistoryValue): value is HistoryObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatFieldLabel(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());
}

function renderHistoryValue(value: HistoryValue): React.ReactNode {
  if (isPlainObject(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return <span className="text-neutral-500">-</span>;
    }

    return (
      <div className="space-y-2">
        {entries.map(([nestedKey, nestedValue]) => (
          <div
            key={nestedKey}
            className="flex items-start justify-between gap-4 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0"
          >
            <span className="text-xl font-gilroy-medium  text-neutral-500 min-w-0">
              {formatFieldLabel(nestedKey)}
            </span>
            <div className="text-xl font-gilroy-medium font-medium text-neutral-500 text-right break-words">
              {renderHistoryValue(nestedValue)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-neutral-500">[]</span>;
    }

    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div
            key={`${index}-${typeof item === 'object' ? JSON.stringify(item) : String(item)}`}
            className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
          >
            {renderHistoryValue(item)}
          </div>
        ))}
      </div>
    );
  }

  return <span>{formatValue(value)}</span>;
}

export const PaymentHistoryModal: React.FC<PaymentHistoryModalProps> = ({
  config: { showModal },
  data: { paymentId },
  eventHandlers: { onClose }
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<any[]>([]);

  const sortedHistory = useMemo(() => {
    return [...history].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [history]);

  useEffect(() => {
    if (!showModal || !paymentId) return;

    setLoading(true);
    getPaymentActivities(paymentId)
      .then((response: any[] | ErrorInfo) => {
        if (!isErrorInfo(response)) {
          setHistory(response || []);
        }
      })
      .finally(() => setLoading(false));
  }, [showModal, paymentId]);

  return (
    <Modal
      config={{ showModal, fullScreen: false, showClose: true, maxWidth: 'md' }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div className="flex flex-col h-[75vh] max-h-[720px]">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 className="text-[1.8rem] font-semibold text-gray-800">Payment History</h2>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-[1.3rem]">
              Loading…
            </div>
          ) : sortedHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
              <img
                src={`${PUBLIC_PATH}/assets/svgs/no-data.svg`}
                alt="no payment history"
                className="w-24 opacity-50"
              />
              <span className="text-[1.3rem]">No payment history records</span>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedHistory.map((item) => {
                const entries = Object.entries(item.changes || {});
                return (
                  <div
                    key={item._id}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[1.35rem] text-gray-800 font-semibold truncate">
                          {item.changedByUsername || 'System Action'}
                        </div>
                        <div className="text-[1.15rem] text-gray-400 mt-1">
                          {moment(item.createdAt).format('MMM DD, YYYY HH:mm')}
                        </div>
                      </div>
                      <div className="text-lg text-neutral-300">#{item.paymentId}</div>
                    </div>

                    {entries.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {entries.map(([key, diff]) => (
                          <div
                            key={key}
                            className="border border-gray-100 rounded-xl p-3 bg-gray-50"
                          >
                            <div className="text-[1.2rem] font-semibold text-gray-700 mb-2">
                              {key}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <div className="text-[1.1rem] text-gray-400 mb-1">Before</div>
                                <div className="text-xl font-gilroy-medium text-neutral-500 bg-white border border-gray-100 rounded-lg p-3">
                                  {renderHistoryValue((diff as any)?.before)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[1.1rem] text-gray-400 mb-1">After</div>
                                <div className="text-xl font-semibold font-gilroy-medium text-neutral-500 bg-white border border-gray-100 rounded-lg p-3">
                                  {renderHistoryValue((diff as any)?.after)}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
